import type { AdminOrderPaymentDto } from '@carcheck/shared';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoGrid, InfoItem } from './InfoBlocks';
import { tokens } from '../../../design';
import { formatDateTime, formatRelative } from '../../../utils/format';
import { describeHoldWindow, hasHoldWindow } from '../_lib/captureEligibility';

/**
 * AUT-219 / AUT-301 — the hold clock, and what it is actually evidence of.
 *
 * A hold is not permanent, and this row is how an operator sees that. What
 * AUT-301 corrected is the CLAIM the row used to make: `holdExpiresAt` is
 * `authorizedAt + escrow.holdDays` — OUR escrow policy, the same key that
 * computes `releaseScheduledFor` — and WebPay exposes no authorization expiry
 * at all. The two numbers have never been compared, and 7 days is very likely
 * SHORTER than the bank's real authorization lifetime. So a past
 * `holdExpiresAt` does NOT mean the money is back on the customer's card; it
 * means our own deadline elapsed and nobody has asked the provider yet.
 *
 * Hence three phases, not two:
 *   - `open`    — grey timestamp, nothing to say.
 *   - `closing` — inside the last 24h (`HOLD_WINDOW_CLOSING_BEFORE_MS`, the
 *     same lead the server's stranded-hold sweep escalates on). Act now.
 *   - `closed`  — red + a banner that says our clock ran out and that capture
 *     is STILL worth pressing, because the server asks the provider before it
 *     refuses. Telling the operator "списание не пройдёт" here was a false
 *     negative: it talked them out of an action that would have worked.
 *
 * Renders nothing at all for a single-stage payment (`hasHoldWindow` false):
 * a "Холд поставлен: —" row would imply a hold that never existed.
 */
export const PaymentHoldWindow = ({
  payment,
  /** Injectable clock so the spec is not wall-clock dependent. */
  now,
}: {
  payment: AdminOrderPaymentDto;
  now?: number;
}): ReactElement | null => {
  const { t } = useTranslation();
  if (!hasHoldWindow(payment)) return null;

  const { phase } = describeHoldWindow(payment.holdExpiresAt, now);
  const closed = phase === 'closed';
  // "6 часов назад" / "через 6 часов" — one placeholder covers both directions
  // because the signed remainder already carries the tense.
  const when = formatRelative(payment.holdExpiresAt, now);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <InfoGrid>
        <InfoItem label={t('orders.detail.payment.authorizedAt')}>
          {formatDateTime(payment.authorizedAt)}
        </InfoItem>
        <InfoItem label={t('orders.detail.payment.holdExpiresAt')}>
          <Box
            component="span"
            data-testid="hold-expires-value"
            data-expired={closed ? 'true' : 'false'}
            data-phase={phase}
            sx={{
              color: closed ? tokens.state.danger : 'inherit',
              fontWeight: closed ? 700 : 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {closed ? <WarningAmberIcon sx={{ fontSize: 14 }} /> : null}
            {formatDateTime(payment.holdExpiresAt)}
          </Box>
        </InfoItem>
      </InfoGrid>

      {closed ? (
        <Alert severity="warning" data-testid="hold-expired-alert" sx={{ py: 0.25 }}>
          {t('orders.detail.payment.holdWindowClosedWarning', { when })}
        </Alert>
      ) : null}

      {phase === 'closing' ? (
        <Alert severity="info" data-testid="hold-closing-alert" sx={{ py: 0.25 }}>
          {t('orders.detail.payment.holdWindowClosingWarning', { when })}
        </Alert>
      ) : null}
    </Box>
  );
};
